import { keepPreviousData, useInfiniteQuery, useQuery, type InfiniteData } from '@tanstack/react-query';
import type { AddedFilter, AdvancedQuery, AssetSummary, AvailabilityFilter, MetadataFilter, MomentDTO, QcFilter, RightsFilter, SavedSearchDTO } from '@prism/contracts';
import { MAX_LIBRARY_OFFSET } from '@prism/contracts';
import { api } from '../client';
import { queryKeys } from '../queryKeys';

export interface LibraryFilters {
  q: string;
  status: string[];
  kind: string[];
  location: string[];
  /** Category ids; 'none' for uncategorised. */
  category: string[];
  /** Metadata facet selections keyed by field id. */
  fields: Record<string, string[]>;
  /** group_order (1.4.0, G1): by season number, then episode number. */
  sort: 'newest' | 'oldest' | 'title' | 'duration' | 'size' | 'group_order';
  includeDismissed: boolean;
  /** 1.6.0: with a search, the moments leave out AI suggestions nobody accepted yet. */
  acceptedOnly?: boolean;
  /** Only assets added today, this week (from Monday) or this month, in the browser's time zone. */
  added?: AddedFilter | null;
  /** Only assets whose metadata is incomplete (a visible Needed field is empty) or complete. */
  metadata?: MetadataFilter | null;
  /** Only assets whose original is online, archived (only in an archive tier), being restored, or offline. */
  availability?: AvailabilityFilter | null;
  /** Only assets whose latest QC passed, had warnings, failed, or was not run (1.0.0-rc.8). */
  qc?: QcFilter | null;
  /** 1.8.0: the Rights quick filter (only while rights management is on; ending_soon uses the workspace's days). */
  rights?: RightsFilter | null;
  /** 1.2.0: Has master (contracts masters.ts MASTER_TAG: v:<variant>, f:<format class>, m:<variant>:<class>). */
  masters?: string[];
  /** Text in chosen fields and conditions on fields (advanced search). */
  advanced?: AdvancedQuery | null;
  /** Only the assets in this collection (the editor's media bin). */
  collection?: string | null;
  /** 1.4.0: only the members of this group and of its sub-groups (?group= in the Library's address). */
  group?: string | null;
}

export interface LibraryResult {
  items: AssetSummary[];
  total: number;
  /** false above 10,000 matches (1.0.0-rc.13): `total` is an estimate ("about 1.2 million"). */
  totalExact?: boolean;
  /** With a text query: matching timecoded moments per asset id. */
  moments?: Record<string, MomentDTO[]>;
  /** Only with facets=true (1.0.0-rc.13 third round); the Library asks useLibraryFacets instead. */
  facets?: Record<string, Record<string, number>>;
  facetsApproximate?: boolean;
  /**
   * Changes when the first page was fetched again (a catalog change, a refetch), not when more pages load: the
   * filter pane's facet counts follow it (useLibraryFacets).
   */
  version?: number;
}

/** The Library's facet counts (GET /api/v1/assets/facets). */
export interface LibraryFacets {
  /** status, kind, location, category, plus one entry per metadata facet field id. */
  facets: Record<string, Record<string, number>>;
  /** true when the counts come from a sample (1.0.0-rc.13): shown with ≈. */
  approximate: boolean;
  computedAt: string;
}

/** A number per first-page object: a refetched first page is a new object, a further page leaves it as it was. */
const pageVersions = new WeakMap<object, number>();
let nextPageVersion = 0;
function versionOf(page: object): number {
  let v = pageVersions.get(page);
  if (v === undefined) { v = ++nextPageVersion; pageVersions.set(page, v); }
  return v;
}

/** The Library lists this many assets per request; more are fetched as the list is scrolled. */
export const LIBRARY_PAGE_SIZE = 100;

/**
 * 1.8.1: the next page's offset, or undefined when there is none: all of them are loaded, or the Library's depth
 * (MAX_LIBRARY_OFFSET rows; the API answers 400 OFFSET_TOO_DEEP beyond it).
 */
export function nextLibraryOffset(last: { items: unknown[]; total: number }, all: Array<{ items: unknown[] }>): number | undefined {
  const loaded = all.reduce((n, p) => n + p.items.length, 0);
  return last.items.length > 0 && loaded < last.total && loaded < MAX_LIBRARY_OFFSET ? loaded : undefined;
}

/** 1.8.1: the list stopped at MAX_LIBRARY_OFFSET with more matches left ("Showing the first 50,000…"). */
export const libraryCapped = (loaded: number, total: number, totalExact?: boolean): boolean =>
  loaded >= MAX_LIBRARY_OFFSET && (total > loaded || totalExact === false);

/** One page of the Library: LIBRARY_PAGE_SIZE assets from `offset` (the list's query string without paging). */
export const fetchLibraryPage = (params: URLSearchParams | string, offset: number): Promise<LibraryResult> =>
  api<LibraryResult>(`/api/v1/assets?${params}&limit=${LIBRARY_PAGE_SIZE}&offset=${offset}`);

/**
 * All loaded pages as one result: the items (without repeats), the total, the first page's version. The total is the
 * first page's (1.2.7): that is the page live events refresh when many pages are loaded (api/events.ts).
 */
function flattenLibrary(data: InfiniteData<LibraryResult>): LibraryResult {
  const seen = new Set<string>();
  const items: AssetSummary[] = [];
  const moments: NonNullable<LibraryResult['moments']> = {};
  for (const page of data.pages) {
    for (const a of page.items) if (!seen.has(a.id)) { seen.add(a.id); items.push(a); }
    Object.assign(moments, page.moments);
  }
  const first = data.pages[0]!;
  return { items, total: first.total, totalExact: first.totalExact, moments, version: versionOf(first) };
}

/** The query string GET /api/v1/assets takes for these filters (without paging). */
export function libraryParams(filters: LibraryFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.q) params.set('q', filters.q);
  for (const k of ['status', 'kind', 'location', 'category'] as const) if (filters[k].length) params.set(k, filters[k].join(','));
  for (const [id, values] of Object.entries(filters.fields)) if (values.length) params.set(`m.${id}`, values.join(','));
  params.set('sort', filters.sort);
  if (filters.includeDismissed) params.set('includeDismissed', 'true');
  if (filters.acceptedOnly) params.set('acceptedOnly', 'true');
  if (filters.added) {
    params.set('added', filters.added);
    params.set('tz', browserTimeZone());
  }
  if (filters.metadata) params.set('metadata', filters.metadata);
  if (filters.availability) params.set('availability', filters.availability);
  if (filters.qc) params.set('qc', filters.qc);
  if (filters.rights) params.set('rights', filters.rights);

  if (filters.masters?.length) params.set('masters', filters.masters.join(','));
  if (filters.advanced) params.set('adv', JSON.stringify(filters.advanced));
  if (filters.collection) params.set('collection', filters.collection);
  if (filters.group) params.set('group', filters.group);
  return params;
}

export function useLibrary(filters: LibraryFilters, opts: { enabled?: boolean; refetchInterval?: number | false } = {}) {
  const params = libraryParams(filters);
  const query = useInfiniteQuery({
    enabled: opts.enabled ?? true,
    ...(opts.refetchInterval ? { refetchInterval: opts.refetchInterval } : {}),
    queryKey: queryKeys.library(params.toString()),
    queryFn: ({ pageParam }) => fetchLibraryPage(params, pageParam),
    initialPageParam: 0,
    // The next page starts after the assets already loaded; no more once all of them are.
    getNextPageParam: nextLibraryOffset,
    select: flattenLibrary,
    placeholderData: keepPreviousData,
  });
  return query;
}

/**
 * The filter pane's facet counts (1.0.0-rc.13 third round), in their own request so the list's page does not wait for
 * them: asked after the page (`after`, the list's version; nothing until it has one) and only while the pane is shown
 * (the pane mounts this hook). A new list version (the first page fetched again) fetches them again; the previous
 * counts stay on screen meanwhile.
 */
export function useLibraryFacets(filters: LibraryFilters, opts: { after: number | undefined; enabled?: boolean }) {
  const params = libraryParams(filters);
  params.delete('sort');
  return useQuery({
    // Not under queryKeys.assets(): those keys hold Library pages, which api/events.ts patches in place.
    queryKey: ['library-facets', params.toString(), opts.after] as const,
    queryFn: ({ signal }) => api<LibraryFacets>(`/api/v1/assets/facets?${params}`, { signal }),
    enabled: (opts.enabled ?? true) && opts.after !== undefined,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Suggestions for a metadata facet's search box: values of `field` containing `text`, prefix matches first,
 * counted among the assets that match the other current filters (GET /api/v1/assets/facet-values).
 */
export function useFacetValues(field: string, text: string, filters: LibraryFilters, opts: { limit?: number; enabled?: boolean } = {}) {
  const params = libraryParams(filters);
  // The library's search text is `search` here; `q` is the suggestion text.
  const search = params.get('q');
  params.delete('q');
  params.delete('sort');
  if (search) params.set('search', search);
  params.set('field', field);
  params.set('q', text);
  if (opts.limit) params.set('limit', String(opts.limit));
  return useQuery({
    queryKey: queryKeys.facetValues(params.toString()),
    queryFn: () => api<{ field: string; items: Array<{ value: string; count: number; atLeast?: boolean }>; approximate?: boolean }>(`/api/v1/assets/facet-values?${params}`),
    enabled: opts.enabled ?? true,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useSavedSearches() {
  return useQuery({ queryKey: queryKeys.savedSearches(), queryFn: () => api<{ items: SavedSearchDTO[] }>('/api/v1/me/saved-searches') });
}
